import { parseDate } from '@internationalized/date';

import ReactSelect from 'dashboard/components/ReactSelect';
import Checkbox from 'design-system/components/Checkbox';
import DateField from 'design-system/components/DatePicker/DateField';
import { Input } from 'design-system/components/Input';
import TextArea from 'design-system/components/TextArea';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

// Ensure that this list is kept consistent with the switch statement below
export const INPUTABLE_FIELD_SCHEMA_TYPES = [
  FieldSchemaType.String,
  FieldSchemaType.Date,
  FieldSchemaType.Choices,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Boolean,
  FieldSchemaType.Number,
  FieldSchemaType.Integer,
  FieldSchemaType.Hash,
  FieldSchemaType.Json,
];

interface FieldInputProps {
  fieldSchema: FieldSchema;
  disabled: boolean;
  onChange: (value?: any) => void;
  onBlur?: () => void;
  id?: string;
  value?: any;
  ariaLabel?: string;
}
const FieldInput = ({
  fieldSchema,
  disabled,
  id,
  value,
  onChange,
  onBlur,
  ariaLabel,
}: FieldInputProps) => {
  switch (fieldSchema.type) {
    case FieldSchemaType.String:
      return (
        <Input
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          id={id}
          value={value}
          onBlur={onBlur}
          aria-labelledby={ariaLabel}
        />
      );
    case FieldSchemaType.Date:
      return (
        <DateField
          id={id}
          disabled={disabled}
          onChange={(value) => {
            onChange(value && value.toString());
          }}
          value={value && parseDate(value)}
        />
      );
    case FieldSchemaType.Choices:
      return (
        <ReactSelect
          id={id}
          disabled={disabled}
          onChange={onChange}
          value={value}
          options={(fieldSchema.config.options || []).map((option) => ({
            value: option,
            label: option,
          }))}
        />
      );
    case FieldSchemaType.MultiChoices:
      return (
        <ReactSelect
          id={id}
          disabled={disabled}
          onChange={onChange}
          value={value}
          options={(fieldSchema.config.options || []).map((option) => ({
            value: option,
            label: option,
          }))}
          isMulti
        />
      );
    case FieldSchemaType.Boolean:
      return (
        <Checkbox id={id} disabled={disabled} onChange={() => onChange(!value)} checked={value} />
      );
    case FieldSchemaType.Number:
      return (
        <Input
          id={id}
          disabled={disabled}
          value={value && value.toString()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              // Blanks out the value
              onChange(null);
            } else {
              onChange(parseFloat(value));
            }
          }}
          type='number'
          // This will allow floats
          step='any'
        />
      );
    case FieldSchemaType.Integer:
      return (
        <Input
          id={id}
          disabled={disabled}
          value={value && value.toString()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              // Blanks out the value
              onChange(null);
            } else {
              onChange(parseInt(value));
            }
          }}
          type='number'
        />
      );
    case FieldSchemaType.Hash:
    case FieldSchemaType.Json:
      return (
        <TextArea
          id={id}
          disabled={disabled}
          value={typeof value === 'string' ? value : JSON.stringify(value)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch (exception) {
              onChange(e.target.value);
            }
          }}
        />
      );
    default:
      return <></>;
  }
};

export default FieldInput;
